import apiClient from './apiClient';

/**
 * Helper function to handle errors
 * @param {object} error - Error object
 * @returns {Error} - Formatted error
 */
const handleError = (error) => {
  const message =
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message;
  if (message) {
    return new Error(message);
  }
  return error;
};

// Flag to use mock data for development
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true';

// Mock profile data
const mockProfile = {
  id: 101,
  email: 'john.smith@example.com',
  firstName: 'John',
  lastName: 'Smith',
  phone: '+1 (555) 123-4567',
  position: 'Senior Developer',
  department: 'Engineering',
  joinDate: '2021-06-15',
  lastLogin: '2023-03-15T08:30:00Z'
};

/**
 * Get user profile
 * @returns {Promise} - Promise containing user profile data
 */
export const getProfile = async () => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => resolve(mockProfile), 500);
    });
  }

  try {
    const response = await apiClient.get('/profile');
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Update user profile
 * @param {object} profileData - Profile data to update
 * @returns {Promise} - Promise containing updated user profile data
 */
export const updateProfile = async (profileData) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      const updatedProfile = { ...mockProfile, ...profileData };
      setTimeout(() => resolve(updatedProfile), 500);
    });
  }

  try {
    const response = await apiClient.put('/profile', profileData);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Change user password
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise} - Promise containing success message
 */
export const changePassword = async (currentPassword, newPassword) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      // Simple validation to simulate API behavior
      if (currentPassword === 'password123') {
        setTimeout(() => resolve({ message: 'Password changed successfully' }), 500);
      } else {
        setTimeout(() => reject(new Error('Current password is incorrect')), 500);
      }
    });
  }

  try {
    const response = await apiClient.post('/profile/change-password', {
      currentPassword,
      newPassword
    });
    const data = response.data.data;
    // The backend rotates the user's security-state binding on a password
    // change. Keep only the freshly bound token returned for this verified
    // current session; every other open session remains revoked.
    if (data?.token) localStorage.setItem('token', data.token);
    return data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Update user avatar/profile picture
 * @param {File} imageFile - Image file to upload
 * @returns {Promise} - Promise containing updated avatar URL
 */
export const updateAvatar = async (imageFile) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => resolve({ 
        avatarUrl: 'https://randomuser.me/api/portraits/men/43.jpg' 
      }), 1000);
    });
  }

  try {
    const formData = new FormData();
    formData.append('avatar', imageFile);
    
    const response = await apiClient.post('/profile/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

const profileService = {
  getProfile,
  updateProfile,
  changePassword,
  updateAvatar
};

export default profileService;
